import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { ArrowLeft, Upload, FileSpreadsheet, FileArchive, Download, CheckCircle2, XCircle } from "lucide-react";

// 25 MB per chunk — well below the typical proxy body-size cap (~100 MB) while
// keeping the number of round-trips manageable even for multi-GB uploads.
const CHUNK_SIZE = 25 * 1024 * 1024;

async function chunkedUpload(file, onProgress, abortRef) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  // 1) init
  const init = await api.post("/admin/uploads/chunked/init", {
    filename: file.name,
    total_size: file.size,
    total_chunks: totalChunks,
  });
  const uploadId = init.data.upload_id;

  // 2) sequential chunk upload (gives a deterministic, accurate progress %)
  let uploadedBytes = 0;
  for (let i = 0; i < totalChunks; i++) {
    if (abortRef?.current) {
      // Best-effort cancel on the server
      try { await api.delete(`/admin/uploads/chunked/${uploadId}`); } catch { /* ignore */ }
      throw new Error("Cancelado pelo usuário");
    }
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end);

    const fd = new FormData();
    fd.append("file", blob, `chunk_${i}.bin`);
    await api.post(`/admin/uploads/chunked/${uploadId}/part/${i}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 5 * 60 * 1000, // 5 min per chunk is plenty for 25 MB
      onUploadProgress: (e) => {
        if (!e.total) return;
        const chunkProgress = e.loaded / e.total;
        const overallBytes = uploadedBytes + chunkProgress * (end - start);
        onProgress(Math.min(overallBytes / file.size, 0.999));
      },
    });
    uploadedBytes += (end - start);
    onProgress(uploadedBytes / file.size);
  }

  // 3) complete
  await api.post(`/admin/uploads/chunked/${uploadId}/complete`);
  onProgress(1);
  return uploadId;
}

function ProgressBar({ pct, label, testid }) {
  const pctNum = Math.round((pct || 0) * 100);
  return (
    <div data-testid={testid} className="mt-3">
      <div className="flex items-center justify-between text-xs text-[#6B6B6B] mb-1">
        <span>{label}</span>
        <span className="tabular-nums">{pctNum}%</span>
      </div>
      <div className="h-1 bg-[#F1EDE7] overflow-hidden">
        <div
          className="h-full bg-[#B8A88A] transition-all duration-200"
          style={{ width: `${pctNum}%` }}
        />
      </div>
    </div>
  );
}

export default function AdminBulkImport() {
  const [excelFile, setExcelFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState(""); // "uploading-excel" | "uploading-zip" | "processing"
  const [excelPct, setExcelPct] = useState(0);
  const [zipPct, setZipPct] = useState(0);
  const [jobProgress, setJobProgress] = useState(null); // { total, processed, accepted, rejected_count }
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const abortRef = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setJobProgress(null);
    if (!excelFile || !zipFile) {
      setError("Envie a planilha e o ZIP de imagens.");
      return;
    }
    setSubmitting(true);
    abortRef.current = false;
    setExcelPct(0);
    setZipPct(0);
    try {
      setStage("uploading-excel");
      const excelUploadId = await chunkedUpload(excelFile, setExcelPct, abortRef);

      setStage("uploading-zip");
      const zipUploadId = await chunkedUpload(zipFile, setZipPct, abortRef);

      setStage("processing");
      const fd = new FormData();
      fd.append("excel_upload_id", excelUploadId);
      fd.append("zip_upload_id", zipUploadId);
      const r = await api.post("/admin/products/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 2 * 60 * 1000, // server returns job_id quickly; processing happens in background
      });
      const jobId = r.data.job_id;

      // Poll the job until it finishes — handles arbitrarily long catalog imports
      // without bumping into Cloudflare's 100s upstream timeout.
      while (true) {
        if (abortRef.current) throw new Error("Cancelado pelo usuário");
        await new Promise((res) => setTimeout(res, 2000));
        const s = await api.get(`/admin/products/bulk-import/jobs/${jobId}`);
        setJobProgress(s.data);
        if (s.data.status === "completed") {
          setResult(s.data);
          break;
        }
        if (s.data.status === "failed") {
          throw new Error(s.data.error || "Job falhou no servidor");
        }
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Erro ao processar importação");
    } finally {
      setSubmitting(false);
      setStage("");
    }
  };

  const cancel = () => { abortRef.current = true; };

  const exportRejectionsCsv = () => {
    if (!result || !result.rejected?.length) return;
    const rows = [["Linha", "Código", "Motivo"]];
    result.rejected.forEach((r) => rows.push([r.linha, r.codigo, r.motivo]));
    const csv = "\ufeff" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prize-joias-rejeicoes-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setExcelFile(null);
    setZipFile(null);
    setResult(null);
    setError("");
    setExcelPct(0);
    setZipPct(0);
  };

  return (
    <div className="max-w-3xl" data-testid="admin-bulk-import">
      <Link to="/admin/produtos" className="overline text-[#6B6B6B] inline-flex items-center gap-2 mb-6">
        <ArrowLeft size={14}/> Voltar
      </Link>
      <p className="overline text-[#A8A8A8] mb-1">Catálogo</p>
      <h1 className="font-display text-4xl mb-3">Cadastro Rápido</h1>
      <p className="text-sm text-[#6B6B6B] mb-10 max-w-2xl leading-relaxed">
        Faça o upload de uma planilha (XLSX ou CSV) com os dados dos produtos e um arquivo ZIP com as
        fotos nomeadas no padrão <code className="font-mono text-[#1A1A1A]">CODIGO(1).jpg</code>,
        <code className="font-mono text-[#1A1A1A]"> CODIGO(2).jpg</code> e assim por diante.
        Arquivos grandes (até vários GB) são enviados automaticamente em pedaços para evitar timeouts.
        As peças importadas são criadas como <strong>Disponível · Peça Única · Novidade</strong> (com expiração em 90 dias).
      </p>

      {!result && (
        <form onSubmit={submit} className="space-y-6" data-testid="bulk-form">
          <div className="bg-white p-6 border border-[#E8E4DF]">
            <p className="overline text-[#A8A8A8] mb-3 inline-flex items-center gap-2">
              <FileSpreadsheet size={14}/> Planilha (.xlsx ou .csv)
            </p>
            <input
              type="file"
              accept=".xlsx,.csv"
              disabled={submitting}
              onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
              data-testid="excel-input"
            />
            {excelFile && (
              <p className="text-xs text-[#6B6B6B] mt-2">
                {excelFile.name} ({(excelFile.size / 1024).toFixed(0)} KB)
              </p>
            )}
            {submitting && stage === "uploading-excel" && (
              <ProgressBar pct={excelPct} label="Enviando planilha…" testid="excel-progress"/>
            )}
            {excelPct === 1 && stage !== "uploading-excel" && (
              <p className="text-xs text-[#3E6A1E] mt-2">✓ Planilha enviada</p>
            )}
          </div>

          <div className="bg-white p-6 border border-[#E8E4DF]">
            <p className="overline text-[#A8A8A8] mb-3 inline-flex items-center gap-2">
              <FileArchive size={14}/> ZIP de imagens
            </p>
            <input
              type="file"
              accept=".zip"
              disabled={submitting}
              onChange={(e) => setZipFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
              data-testid="zip-input"
            />
            {zipFile && (
              <p className="text-xs text-[#6B6B6B] mt-2">
                {zipFile.name} ({(zipFile.size / 1024 / 1024).toFixed(1)} MB ·
                {" "}{Math.ceil(zipFile.size / CHUNK_SIZE)} pedaços de até 25 MB)
              </p>
            )}
            {submitting && stage === "uploading-zip" && (
              <ProgressBar pct={zipPct} label="Enviando imagens…" testid="zip-progress"/>
            )}
          </div>

          {stage === "processing" && (
            <div className="bg-[#F5EFE8] border border-[#B8A88A] p-5 text-sm text-[#4A4A4A]" data-testid="processing-banner">
              <p className="font-medium mb-2">Processando catálogo no servidor…</p>
              {jobProgress ? (
                <>
                  <ProgressBar
                    pct={(jobProgress.processed || 0) / Math.max(jobProgress.total || 1, 1)}
                    label={`${jobProgress.processed || 0} de ${jobProgress.total || 0} linhas processadas`}
                    testid="job-progress"
                  />
                  <p className="text-xs text-[#6B6B6B] mt-2">
                    ✓ {jobProgress.accepted || 0} cadastradas · ✕ {jobProgress.rejected_count || 0} rejeitadas
                  </p>
                </>
              ) : (
                <p className="text-xs text-[#6B6B6B]">Iniciando processamento…</p>
              )}
            </div>
          )}

          {error && <p className="text-sm text-[#B85050]" data-testid="bulk-error">{error}</p>}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting || !excelFile || !zipFile}
              data-testid="bulk-submit"
              className="btn-primary inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload size={14} className="mr-2"/>
              {submitting ? "Enviando…" : "Iniciar importação"}
            </button>
            {submitting && (
              <button type="button" onClick={cancel} className="btn-secondary" data-testid="bulk-cancel">
                Cancelar
              </button>
            )}
          </div>
        </form>
      )}

      {result && (
        <div className="space-y-6" data-testid="bulk-result">
          <div className="bg-white p-8 border border-[#E8E4DF]">
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <Stat icon={<CheckCircle2 size={22} className="text-[#3E6A1E]"/>} label="Cadastradas com sucesso" value={result.accepted} testid="stat-accepted"/>
              <Stat icon={<XCircle size={22} className="text-[#B85050]"/>} label="Linhas rejeitadas" value={result.rejected_count} testid="stat-rejected"/>
              <Stat icon={null} label="Total processadas" value={result.total} testid="stat-total"/>
            </div>

            {result.rejected_count > 0 && (
              <>
                <div className="flex items-center justify-between mb-3 mt-8">
                  <h3 className="font-display text-xl">Detalhamento das rejeições</h3>
                  <button onClick={exportRejectionsCsv} className="btn-secondary inline-flex items-center" data-testid="export-rejections-btn">
                    <Download size={14} className="mr-2"/> Exportar CSV
                  </button>
                </div>
                <div className="border border-[#E8E4DF] overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAF9F7] text-left">
                      <tr>
                        <th className="p-3 overline text-[#A8A8A8]">Linha</th>
                        <th className="p-3 overline text-[#A8A8A8]">Código</th>
                        <th className="p-3 overline text-[#A8A8A8]">Motivo</th>
                      </tr>
                    </thead>
                    <tbody data-testid="rejected-rows">
                      {result.rejected.map((r, i) => (
                        <tr key={i} className="border-t border-[#F1EDE7]">
                          <td className="p-3 font-mono text-xs text-[#6B6B6B]">{r.linha}</td>
                          <td className="p-3 font-mono text-xs">{r.codigo}</td>
                          <td className="p-3 text-[#1A1A1A]">{r.motivo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div className="flex gap-3">
            <button onClick={reset} className="btn-primary" data-testid="bulk-new">Nova importação</button>
            <Link to="/admin/produtos" className="btn-secondary">Voltar à lista de produtos</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div className="bg-[#FAF9F7] border border-[#E8E4DF] p-5" data-testid={testid}>
      <div className="flex items-center gap-3 mb-2">{icon}<p className="overline text-[#A8A8A8]">{label}</p></div>
      <p className="font-display text-4xl">{value ?? 0}</p>
    </div>
  );
}
